/*
 * Atiems – „Prekė pridėta į krepšelį“ langas.
 *
 * Prestige's product form already adds items with fetch('/cart/add.js'). With cartType "page" it then redirects
 * to /cart; with "message" it instead fires `variant:add` (success) or `cart:error` (failure) on the form and
 * `cart:change` on the document (header count). We switch to "message" here, so if this file never loads the
 * theme keeps its original redirect, and we render our own dialog from those events.
 */
(() => {
  const dialog = document.getElementById('AtiemsAtcDialog');
  const vars = window.themeVariables;
  if (!dialog || typeof dialog.showModal !== 'function' || !vars?.settings) return;

  vars.settings.cartType = 'message';
  document.documentElement.classList.add('atiems-atc-ready');

  const $ = (selector) => dialog.querySelector(selector);
  const successState = $('[data-atc-success]');
  const errorState = $('[data-atc-error]');
  const image = $('[data-atc-image]');
  const checkoutLink = $('[data-atc-checkout]');
  const termsHint = $('[data-atc-terms-hint]');
  const pending = new Map(); // form -> { submitter, quantity, timer }
  const NETWORK_TIMEOUT = 15000;
  let returnFocusTo = null;
  let closing = false;

  /* ---------- Formatting ---------- */

  const money = (cents) => {
    const format = String(vars.settings.moneyFormat || '€{{amount_with_comma_separator}}').replace(/<[^>]*>/g, '');
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      const noDecimals = key.includes('no_decimals');
      const [integer, decimals] = (cents / 100).toFixed(noDecimals ? 0 : 2).split('.');
      const comma = key === 'amount_with_comma_separator' || key === 'amount_no_decimals_with_comma_separator';
      const thousands = comma ? '.' : key === 'amount_with_period_and_space' ? ' ' : key === 'amount_with_apostrophe_separator' ? "'" : ',';
      const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals ? `${grouped}${comma ? ',' : '.'}${decimals}` : grouped;
    });
  };

  // 1 prekė, 2–9 prekės, 10–20 prekių, 21 prekė …
  const itemsWord = (count) => {
    const lastDigit = count % 10;
    const lastTwo = count % 100;
    if (lastDigit === 1 && lastTwo !== 11) return 'prekė';
    if (lastDigit >= 2 && lastDigit <= 9 && (lastTwo < 10 || lastTwo >= 20)) return 'prekės';
    return 'prekių';
  };

  const sizedImageUrl = (url, width) => {
    if (!url) return '';
    const absolute = url.startsWith('//') ? `https:${url}` : url;
    return `${absolute}${absolute.includes('?') ? '&' : '?'}width=${width}`;
  };

  // Shopify already answers in the storefront language (lt), e.g. „…pridėta tik 3 prekės (-ių).“, so its own
  // message is shown as is; only English fallbacks and network failures get our wording.
  const errorMessage = (raw) => {
    const message = typeof raw === 'string' ? raw.trim() : '';
    const text = message.toLowerCase();
    const email = dialog.dataset.contactEmail;
    if (raw === 'network') {
      return 'Nepavyko susisiekti su parduotuve. Patikrinkite interneto ryšį ir bandykite dar kartą.';
    }
    if (/sold out|out of stock|unavailable/.test(text)) {
      return 'Šios prekės šiuo metu nebėra sandėlyje.';
    }
    if (/maximum|already|all \d+|can.?t add more|cannot add more/.test(text)) {
      return 'Krepšelyje jau yra visas turimas šios prekės kiekis, todėl daugiau pridėti negalima.';
    }
    if (message && /[ąčęėįšųūž]/i.test(message)) return message;
    return `Nepavyko pridėti prekės į krepšelį. Pabandykite dar kartą${email ? ` arba parašykite mums ${email}` : ''}.`;
  };

  // Shopify adds what is in stock and still reports an error, so the cart did change in that case.
  const isPartialAdd = (raw) => typeof raw === 'string' && /pridėta tik|only \d+ .*added/i.test(raw);

  /* ---------- Dialog ---------- */

  const open = (state, labelledBy) => {
    successState.hidden = state !== 'success';
    errorState.hidden = state !== 'error';
    dialog.setAttribute('aria-labelledby', labelledBy);
    dialog.setAttribute('aria-describedby', state === 'success' ? 'AtiemsAtcQuestion' : 'AtiemsAtcErrorText');
    checkoutLink.removeAttribute('aria-disabled');
    checkoutLink.classList.remove('is-loading');
    termsHint.hidden = true;
    closing = false;
    dialog.classList.remove('is-closing');

    if (!dialog.open) dialog.showModal();
    document.getElementById(labelledBy)?.focus({ preventScroll: true });
  };

  const close = () => {
    if (!dialog.open || closing) return;
    closing = true;
    const finish = () => {
      dialog.classList.remove('is-closing');
      if (dialog.open) dialog.close();
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return finish();
    dialog.classList.add('is-closing');
    setTimeout(finish, 180);
  };

  dialog.addEventListener('close', () => {
    closing = false;
    const target = returnFocusTo;
    returnFocusTo = null;
    if (target?.isConnected) target.focus({ preventScroll: true });
  });

  // Escape: animate instead of the instant native close.
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });

  dialog.addEventListener('change', (event) => {
    if (event.target.matches('.rterms-checkbox') && event.target.checked) termsHint.hidden = true;
  });

  dialog.addEventListener('click', (event) => {
    if (event.target.closest('[data-atc-close]') || event.target === dialog) close();
  });

  // Window capture runs before the store's terms app (R-Terms), which injects its checkbox next to checkout
  // links and silently cancels the click while it is unticked, so we explain why nothing happened.
  window.addEventListener('click', (event) => {
    if (!event.target.closest?.('[data-atc-checkout]')) return;
    if (checkoutLink.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }
    const terms = dialog.querySelector('.rterms-checkbox');
    if (terms && !terms.checked) {
      event.preventDefault();
      termsHint.hidden = false;
      terms.focus();
      return;
    }
    checkoutLink.setAttribute('aria-disabled', 'true');
    checkoutLink.classList.add('is-loading');
  }, true);

  // Coming back from checkout with the browser's Back button restores the page as it was left.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    checkoutLink.removeAttribute('aria-disabled');
    checkoutLink.classList.remove('is-loading');
  });

  /* ---------- Content ---------- */

  const showSuccess = (detail, request) => {
    const item = detail?.items?.[0];
    if (!item) return;

    const quantity = request?.quantity || item.quantity || 1;
    const imageUrl = item.featured_image?.url || item.image;
    image.hidden = !imageUrl;
    if (imageUrl) {
      image.src = sizedImageUrl(imageUrl, 240);
      image.alt = item.featured_image?.alt || item.product_title || '';
    }

    $('[data-atc-name]').textContent = item.product_title || item.title || '';

    const options = (item.options_with_values || [])
      .filter((option) => option.name !== 'Title' && option.value !== 'Default Title')
      .map((option) => `${option.name}: ${option.value}`);
    const variantText = options.length
      ? options.join(' · ')
      : item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    const variant = $('[data-atc-variant]');
    variant.textContent = variantText;
    variant.hidden = !variantText;

    $('[data-atc-qty]').textContent = `Kiekis: ${quantity}`;
    $('[data-atc-price]').textContent = money(item.final_price * quantity);
    const unit = $('[data-atc-unit]');
    unit.textContent = `${money(item.final_price)} / vnt.`;
    unit.hidden = quantity <= 1;

    const cart = detail.cart;
    $('[data-atc-summary]').textContent = cart
      ? `Krepšelyje: ${cart.item_count} ${itemsWord(cart.item_count)} · Suma: ${money(cart.total_price)}`
      : '';

    open('success', 'AtiemsAtcTitle');
  };

  const showError = (raw) => {
    $('[data-atc-error-text]').textContent = errorMessage(raw);
    $('#AtiemsAtcErrorTitle').textContent = isPartialAdd(raw) ? 'Pridėtas ne visas kiekis' : 'Prekės pridėti nepavyko';
    open('error', 'AtiemsAtcErrorTitle');
  };

  /* ---------- Request tracking (one request per form, no duplicate popups) ---------- */

  const isAddForm = (form) => form instanceof HTMLFormElement && /\/cart\/add(\.js)?($|\?)/.test(new URL(form.action, location.origin).pathname);

  const settle = (form) => {
    const request = pending.get(form);
    if (!request) return null;
    clearTimeout(request.timer);
    pending.delete(form);
    return request;
  };

  const failPending = (form) => {
    const request = settle(form);
    if (!request) return;
    // The theme leaves its buttons busy when the request itself throws.
    Array.from(form.elements).forEach((element) => element.type === 'submit' && element.removeAttribute('aria-busy'));
    returnFocusTo = request.submitter;
    showError('network');
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!isAddForm(form)) return;

    // Rapid clicks / repeated Enter: ignore while this form's request is still running.
    if (pending.has(form) || dialog.open) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (!form.checkValidity()) return; // the theme reports the invalid field itself

    // Collection quick-add forms ask the theme to force the cart drawer open; the dialog replaces it.
    form.querySelectorAll('input[name="on_success"][value="force_open_drawer"]').forEach((input) => { input.disabled = true; });

    const quantity = Number.parseInt(new FormData(form).get('quantity'), 10) || 1;
    pending.set(form, {
      submitter: event.submitter || document.activeElement,
      quantity,
      timer: setTimeout(() => failPending(form), NETWORK_TIMEOUT),
    });
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    if (pending.size && event.reason instanceof TypeError) [...pending.keys()].forEach(failPending);
  });

  document.addEventListener('variant:add', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : event.target.closest?.('form');
    const request = form ? settle(form) : null;
    if (vars.settings.pageType === 'cart') return; // the cart page reloads itself

    // A quick-buy modal must not stay open underneath the confirmation.
    document.querySelectorAll('quick-buy-modal[open]').forEach((modal) => modal.hide?.());
    returnFocusTo = request?.submitter || document.activeElement;
    showSuccess(event.detail, request);
  });

  document.addEventListener('cart:error', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : event.target.closest?.('form');
    if (!form || !isAddForm(form)) return;
    const request = settle(form);
    returnFocusTo = request?.submitter || document.activeElement;
    showError(event.detail?.error);
  });
})();
